import { useState, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useConfigurationStore, selectAddOnCount } from '@store/configurationStore';
import { useAddOnsForPlan } from '@hooks/useAddOnsForPlan';
import { isTmfOfferingId } from '@utils/productOfferingMapper';
import type { ParsedAddOn } from '@t/addOn.types';

export const useAddOnStep = () => {
  const { activePlanConfig, selectedAddOns, toggleAddOn, updateAddOnQuantity, editMode } = useConfigurationStore(
    useShallow((s) => ({
      activePlanConfig: s.activePlanConfig,
      selectedAddOns: s.selectedAddOns,
      toggleAddOn: s.toggleAddOn,
      updateAddOnQuantity: s.updateAddOnQuantity,
      editMode: s.editMode,
    }))
  );
  const addOnCount = useConfigurationStore(selectAddOnCount);
  const [showError, setShowError] = useState(false);

  const planId = activePlanConfig?.planId ?? '';
  const sessionId = activePlanConfig?.sessionId ?? '';
  const homeId = activePlanConfig?.homeIdOfQualifiedOffers ?? '';

  const addOnsData = useAddOnsForPlan(sessionId, homeId, planId);

  const themePackageCodes = new Set(addOnsData.themePackages.map((a) => a.code));
  const individualChannelCodes = new Set(addOnsData.individualChannels.map((a) => a.code));

  const themePackageCount = selectedAddOns
    .filter((a) => themePackageCodes.has(a.code))
    .reduce((sum, a) => sum + a.quantity, 0);

  const individualChannelCount = selectedAddOns
    .filter((a) => individualChannelCodes.has(a.code))
    .reduce((sum, a) => sum + a.quantity, 0);

  const isSelected = useCallback((code: string) => selectedAddOns.some((a) => a.code === code), [selectedAddOns]);

  const getQuantity = useCallback(
    (code: string) => selectedAddOns.find((a) => a.code === code)?.quantity ?? 1,
    [selectedAddOns]
  );

  const handleToggle = useCallback(
    (addOn: ParsedAddOn) => {
      setShowError(false);
      toggleAddOn(addOn.code);
    },
    [toggleAddOn]
  );

  const handleQuantityChange = useCallback(
    (code: string, qty: number) => {
      updateAddOnQuantity(code, qty);
    },
    [updateAddOnQuantity]
  );

  const validateAndSubmit = useCallback(
    (onAddToPlan: () => void, minRequired = 1) => {
      if (!editMode && minRequired > 0 && selectedAddOns.length === 0) {
        setShowError(true);
        return;
      }
      onAddToPlan();
    },
    [selectedAddOns, editMode]
  );

  // TMF only defines a selection minimum on bundledGroupProductOffering (the equipment pick-group
  // handled by EquipmentSelectionStep) — the plain optional add-ons shown here have no catalog-defined
  // minimum, so TMF plans never require a selection. GLDS has no such concept at all, so it keeps the
  // pre-existing "at least 1" requirement. planId is a UUID for TMF offerings, numeric for GLDS.
  const isTmfPlan = isTmfOfferingId(activePlanConfig?.planId);

  return {
    addOnCount,
    themePackageCount,
    individualChannelCount,
    showError,
    isSelected,
    getQuantity,
    handleToggle,
    handleQuantityChange,
    validateAndSubmit,
    isTmfPlan,
    ...addOnsData,
  };
};
